import React, { useState, useCallback, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { startOfMonth, endOfMonth, format, subYears, eachDayOfInterval } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import Switch from 'react-switch';  // react-switch 사용
import config from '../../../config';
import { CSVLink } from 'react-csv';
import { parseEvSensorData  } from '../../../utils/parseEvSensorData ';
import { fetchEv_list } from '../../../store';




// 고유 색상 생성 함수 (HSL을 이용하여 유사한 색상을 피함)
const generateColor = (index, total) => {
    const hue = Math.floor((360 / total) * index); // 색상 범위(0-360도) 내에서 인덱스에 따라 고유한 hue 생성
    return `hsl(${hue}, 70%, 50%)`; // 채도와 밝기를 고정하여 고유한 색상 반환
};

const GenMonth = () => {
    const dispatch = useDispatch();
    const ivList = useSelector(state => state.Iv_list);
    const Ev_list = useSelector((state) => state.Ev_list || {});
    const [viewMode, setViewMode] = useState('total');
    const [displayType, setDisplayType] = useState('chart');
    const [tempViewMode, setTempViewMode] = useState('total');
    const [tempDisplayType, setTempDisplayType] = useState('chart');
    const [startMonth, setStartMonth] = useState(new Date());
    const [endMonth, setEndMonth] = useState(new Date());
    const [displayedData, setDisplayedData] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [lastSearchMode, setLastSearchMode] = useState('total');

    useEffect(() => {
              dispatch(fetchEv_list());
            }, [dispatch]);

    const handleSearch = useCallback(async () => {
        const startDate = startOfMonth(startMonth);
        const endDate = endOfMonth(endMonth);


        try {
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            
            // 요청 바디 공통
            const body = JSON.stringify({ start_date: startDateStr, end_date: endDateStr });

            // 병렬 요청: 인버터 + 환경센서
            const [ivRes, envRes] = await Promise.all([
                fetch(`${config.backendUrl}/get_day_Iv_data`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                }),
                fetch(`${config.backendUrl}/get_hourly_Ev_mrt_data`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                }),
            ]);

            // 응답 체크
            if (!ivRes.ok || !envRes.ok) {
                throw new Error(`요청 실패: IV ${ivRes.status}, ENV ${envRes.status}`);
            }

            // 데이터 파싱
            const data = await ivRes.json();
            const envData = await envRes.json();
     

            const allDates = eachDayOfInterval({
                            start: new Date(startDate),
                            end: new Date(endDate),
                        }).map(date => format(date, 'yyyy-MM-dd'));
            
            // 월 리스트 생성 (ex. 2025-05, 2025-06 ...)
            const allMonths = [];
            let currentMonth = startDate;

            while (currentMonth <= endDate) {
                allMonths.push(format(currentMonth, 'yyyy-MM'));
                currentMonth = startOfMonth(currentMonth.setMonth(currentMonth.getMonth() + 1));
            }

            const { horizontalIrradiance, tiltedIrradiance, surfaceTemp, outdoorTemp } =
                parseEvSensorData(envData, allDates, Ev_list);

            // 월별 평균 직접 계산
            const avgHorizontal = [];
            const avgTilted = [];
            const avgSurfaceTemp = [];
            const avgOutdoorTemp = [];

            const dateIndexPairs = allDates.map((dateStr, i) => ({ i, date: new Date(dateStr) }));

            const getAvg = (arr, indices) =>
                indices.length
                    ? (indices.map(i => arr[i]).filter(v => v !== null && !isNaN(v)).reduce((sum, v) => sum + v, 0) / indices.length)
                    : 0;

            allMonths.forEach(monthStr => {
                const indices = dateIndexPairs
                    .filter(({ date }) => format(date, 'yyyy-MM') === monthStr)
                    .map(({ i }) => i);

                avgHorizontal.push(Number(getAvg(horizontalIrradiance, indices).toFixed(2)));
                avgTilted.push(Number(getAvg(tiltedIrradiance, indices).toFixed(2)));
                avgSurfaceTemp.push(Number(getAvg(surfaceTemp, indices).toFixed(2)));
                avgOutdoorTemp.push(Number(getAvg(outdoorTemp, indices).toFixed(2)));
            });


            let formattedCsvData = [];

            if (tempViewMode === 'total') {
                // 전체 합계 CSV 형식
                formattedCsvData.push([
                    '날짜',
                    '전체 합계 (kW)',
                    '발전시간 (h)',
                    '경사 일사량 (W/m²)',
                    '수평 일사량 (W/m²)',
                    '모듈온도 (℃)',
                    '외기온도 (℃)'
                ]);
                const totalData = allMonths.map(month => {
                    const monthStart = startOfMonth(new Date(month));
                    const monthEnd = endOfMonth(new Date(month));

                    return ivList.seq.reduce((sum, seq) => {
                        const monthData = data.data.filter(item => {
                            const itemDate = new Date(item.created_at.split(' ')[0]);
                            return item.seq === seq && itemDate >= monthStart && itemDate <= monthEnd;
                        });

                        return sum + monthData.reduce((innerSum, item) => innerSum + item.Iv_Add_power, 0);
                    }, 0);
                });

                setDisplayedData({
                    labels: allMonths,
                    datasets: [
                        {
                        label: '전체 합계',
                        data: totalData,
                        backgroundColor: 'rgba(255, 99, 132, 0.6)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1,
                        type: 'bar'
                        },
                        {
                        label: '경사 일사량 평균',
                        data: avgTilted,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y1'
                        },
                        {
                        label: '수평 일사량 평균',
                        data: avgHorizontal,
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y1'
                        },
                        {
                        label: '모듈온도 평균',
                        data: avgSurfaceTemp,
                        borderColor: 'rgba(255, 206, 86, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y2'
                        },
                        {
                        label: '외기온도 평균',
                        data: avgOutdoorTemp,
                        borderColor: 'rgba(153, 102, 255, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y2'
                        }
                    ]
                    });

                // CSV 데이터 포맷
                formattedCsvData = formattedCsvData.concat(allMonths.map((month, index) => {
                    const powerKwh = (totalData[index] / 1000).toFixed(2) + ' kW';
                    const genHour = (totalData[index] / ivList.iv_capa.reduce((sum, capa) => sum + capa, 0) / 1000).toFixed(2) + ' h';

                    const safeTilted = avgTilted[index] + ' W/m²';
                    const safeHorizontal = avgHorizontal[index] + ' W/m²';
                    const safeSurface = avgSurfaceTemp[index] + ' ℃';
                    const safeOutdoor = avgOutdoorTemp[index] + ' ℃';

                    // const safeHorizontal = avgHorizontal[index] !== undefined ? avgHorizontal[index].toFixed(2) + ' W/m²' : '';
                    // const safeSurface = avgSurfaceTemp[index] !== undefined ? avgSurfaceTemp[index].toFixed(1) + ' ℃' : '';
                    // const safeOutdoor = avgOutdoorTemp[index] !== undefined ? avgOutdoorTemp[index].toFixed(1) + ' ℃' : '';


                    

                    return [month, powerKwh, genHour, safeTilted, safeHorizontal, safeSurface, safeOutdoor];
                }));
                

                // 합계 추가
                const totalPowerSum = totalData.reduce((sum, value) => sum + value, 0);
                const totalHours = totalPowerSum / ivList.iv_capa.reduce((sum, capa) => sum + capa, 0) / 1000;

                const sumSafe = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) : 0;

                formattedCsvData.push([
                '합계',
                (totalPowerSum / 1000).toFixed(2) + ' kW',
                totalHours.toFixed(2) + ' h',
                sumSafe(avgTilted) + ' W/m²',
                sumSafe(avgHorizontal) + ' W/m²',
                sumSafe(avgSurfaceTemp) + ' ℃',
                sumSafe(avgOutdoorTemp) + ' ℃'
                // sumSafe(avgTilted).toFixed(0) + ' W/m²',
                // sumSafe(avgHorizontal).toFixed(0) + ' W/m²',
                // sumSafe(avgSurfaceTemp).toFixed(1) + ' ℃',
                // sumSafe(avgOutdoorTemp).toFixed(1) + ' ℃'
                ]);

            } else {
                // 개별 보기 CSV 형식
                formattedCsvData.push([
                    '날짜',
                    ...ivList.iv_name.map(name => `${name} (kW)`),
                    '전체 합계 (kW)',
                    '발전시간 (h)',
                    '경사 일사량 (W/m²)',
                    '수평 일사량 (W/m²)',
                    '모듈온도 (℃)',
                    '외기온도 (℃)'
                ]);
                const inverterDatasets = ivList.seq.map((seq, index) => {
                    return allMonths.map(month => {
                        const monthStart = startOfMonth(new Date(month));
                        const monthEnd = endOfMonth(new Date(month));

                        const monthData = data.data.filter(item => {
                            const itemDate = new Date(item.created_at.split(' ')[0]);
                            return item.seq === seq && itemDate >= monthStart && itemDate <= monthEnd;
                        });

                        return monthData.reduce((sum, item) => sum + item.Iv_Add_power, 0);
                    });
                });

                setDisplayedData({
                    labels: allMonths,
                    datasets: [
                        ...ivList.seq.map((seq, index) => ({
                        label: ivList.iv_name[index],
                        data: inverterDatasets[index],
                        backgroundColor: generateColor(index, ivList.seq.length),
                        borderColor: generateColor(index, ivList.seq.length),
                        borderWidth: 2,
                        fill: false,
                        yAxisID: 'y'  // 발전량
                        })),
                        {
                        label: '경사 일사량 평균',
                        data: avgTilted,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y1'
                        },
                        {
                        label: '수평 일사량 평균',
                        data: avgHorizontal,
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y1'
                        },
                        {
                        label: '모듈온도 평균',
                        data: avgSurfaceTemp,
                        borderColor: 'rgba(255, 206, 86, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y2'
                        },
                        {
                        label: '외기온도 평균',
                        data: avgOutdoorTemp,
                        borderColor: 'rgba(153, 102, 255, 1)',
                        borderWidth: 2,
                        fill: false,
                        type: 'line',
                        yAxisID: 'y2'
                        }
                    ]
                });

                allMonths.forEach((month, index) => {
                    const totalPower = inverterDatasets.reduce((sum, dataset) => sum + dataset[index], 0);
                    const generationHours = totalPower / ivList.iv_capa.reduce((sum, capa) => sum + capa, 0) / 1000;

                    formattedCsvData.push([
                        month,
                        ...inverterDatasets.map(dataset => (dataset[index] / 1000).toFixed(2) + ' kW'),
                        (totalPower / 1000).toFixed(2) + ' kW',
                        generationHours.toFixed(2) + ' h',
                        avgTilted[index] + ' W/m²',
                        avgHorizontal[index] + ' W/m²',
                        avgSurfaceTemp[index] + ' ℃',
                        avgOutdoorTemp[index] + ' ℃'
                    ]);
                });

                // 합계 추가
                const columnSums = inverterDatasets.map(dataset => dataset.reduce((sum, value) => sum + value, 0));
                const totalPowerSum = columnSums.reduce((sum, value) => sum + value, 0);
                const totalHours = totalPowerSum / ivList.iv_capa.reduce((sum, capa) => sum + capa, 0) / 1000;

                const average = arr => (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2);

                formattedCsvData.push([
                '합계',
                ...columnSums.map(sum => (sum / 1000).toFixed(2) + ' kW'),
                (totalPowerSum / 1000).toFixed(2) + ' kW',
                totalHours.toFixed(2) + ' h',
                average(avgTilted) + ' W/m²',
                average(avgHorizontal) + ' W/m²',
                average(avgSurfaceTemp) + ' ℃',
                average(avgOutdoorTemp) + ' ℃'
                ]);
            }

            setCsvData(formattedCsvData);

            setLastSearchMode(tempViewMode);
            setViewMode(tempViewMode);
            setDisplayType(tempDisplayType);

        } catch (error) {
            console.error(error);
            alert('데이터를 가져오는 중 오류가 발생했습니다.');
            setDisplayedData(null);
        }
    }, [startMonth, endMonth, ivList, tempViewMode, tempDisplayType]);

    return (
    <div>
      <div className="controls-container">
        {/* 날짜 선택 */}
        <div className="date-picker-group">
          <DatePicker
            selected={startMonth}
            onChange={(date) => setStartMonth(date)}
            dateFormat="yyyy-MM"
            showMonthYearPicker
            minDate={subYears(new Date(), 3)}
            maxDate={new Date()}
            locale={ko}
            customInput={<input className="date-input" />}
          />
          <div>~</div>
          <DatePicker
            selected={endMonth}
            onChange={(date) => setEndMonth(date)}
            dateFormat="yyyy-MM"
            showMonthYearPicker
            minDate={subYears(new Date(), 3)}
            maxDate={new Date()}
            locale={ko}
            customInput={<input className="date-input" />}
          />
        </div>

        {/* 검색 버튼 */}
        <button onClick={handleSearch} className="styled-button">검색</button>

        {/* 전체 합계 / 개별 보기 슬라이드 버튼 */}
        <div className="toggle-container">
          <span className="toggle-label">개별 보기</span>
          <Switch
            className="daily-toggle-switch"
            onChange={() => setTempViewMode(tempViewMode === "individual" ? "total" : "individual")}
            checked={tempViewMode === "total"}
            uncheckedIcon={false}
            checkedIcon={false}
            onColor="#86d3ff"
            offColor="#86d3ff"
            onHandleColor="#0D6EFD"
            offHandleColor="#0D6EFD"
          />
          <span className="toggle-label">전체 합계</span>
        </div>

        {/* 그래프 / 표 보기 슬라이드 버튼 */}
        <div className="toggle-container">
          <span className="toggle-label">표 보기</span>
          <Switch
            onChange={() => setTempDisplayType(tempDisplayType === "table" ? "chart" : "table")}
            checked={tempDisplayType === "chart"}
            uncheckedIcon={false}
            checkedIcon={false}
            onColor="#86d3ff"
            offColor="#86d3ff"
            onHandleColor="#0D6EFD"
            offHandleColor="#0D6EFD"
          />
          <span className="toggle-label">그래프 보기</span>
        </div>

        {/* CSV 다운로드 버튼 */}
        <CSVLink data={csvData} filename="generation-data.csv" className="csv-download">
          CSV 다운로드
        </CSVLink>
      </div>

      {/* ChartComponent 또는 TableComponent 렌더링 */}
      {displayType === "chart" ? (
        <ChartComponent displayedData={displayedData} lastSearchMode={lastSearchMode} />
      ) : (
        <TableComponent displayedData={displayedData} viewMode={viewMode} ivList={ivList} />
      )}
    </div>
  );
};
















const ChartComponent = ({ displayedData, lastSearchMode }) => {
    return (
        <div className="chartHour-chart-container" style={{width: "90%"}}>
            {displayedData && displayedData.labels ? (
                lastSearchMode === 'total' ? (  // 마지막 검색 모드에 따라 차트 렌더링
                    <Bar
                        data={displayedData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    position: 'top',  // 범례를 차트 위쪽에 표시
                                },
                                tooltip: {
                                    callbacks: {
                                        label: function (tooltipItem) {
                                        const label = tooltipItem.dataset.label;
                                        const value = tooltipItem.raw;

                                        let unit = 'kW';
                                        let displayValue = value / 1000;

                                        if (label.includes('일사량')) {
                                            unit = 'W/m²';
                                            displayValue = value;
                                        } else if (label.includes('온도')) {
                                            unit = '℃';
                                            displayValue = value;
                                        }

                                        return `${label}: ${displayValue.toFixed(2)} ${unit}`;
                                        },
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    title: { display: true },
                                },
                                y: {
                                    title: { display: true, text: '발전량 (kW)' },
                                    position: 'left',
                                    ticks: {
                                    callback: (value) => value / 1000 + ' kW',
                                    },
                                },
                                y1: {
                                    title: { display: true, text: '일사량 (W/m²)' },
                                    position: 'right',
                                    grid: { drawOnChartArea: false },
                                },
                                y2: {
                                    title: { display: true, text: '온도 (℃)' },
                                    position: 'right',
                                    grid: { drawOnChartArea: false },
                                    ticks: { color: 'rgba(153, 102, 255)' }
                                }
                            }
                        }}
                        width={800}
                        height={400}
                    />
                ) : (
                    <Line
                        data={displayedData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    position: 'top',  // 범례를 차트 위쪽에 표시
                                },
                                tooltip: {
                                    callbacks: {
                                        label: function (tooltipItem) {
                                        const label = tooltipItem.dataset.label;
                                        const value = tooltipItem.raw;

                                        let unit = 'kW';
                                        let displayValue = value / 1000;

                                        if (label.includes('일사량')) {
                                            unit = 'W/m²';
                                            displayValue = value;
                                        } else if (label.includes('온도')) {
                                            unit = '℃';
                                            displayValue = value;
                                        }

                                        return `${label}: ${displayValue.toFixed(2)} ${unit}`;
                                        },
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    title: { display: true },
                                },
                                y: {
                                    title: { display: true, text: '발전량 (kW)' },
                                    position: 'left',
                                    ticks: {
                                    callback: (value) => value / 1000 + ' kW',
                                    },
                                },
                                y1: {
                                    title: { display: true, text: '일사량 (W/m²)' },
                                    position: 'right',
                                    grid: { drawOnChartArea: false },
                                },
                                y2: {
                                    title: { display: true, text: '온도 (℃)' },
                                    position: 'right',
                                    grid: { drawOnChartArea: false },
                                    ticks: { color: 'rgba(153, 102, 255)' }
                                }
                            },
                            elements: {
                                line: {
                                    tension: 0.4,  // 곡선 라인 적용, 0.4 정도가 적당히 곡선을 이룸
                                },
                                point: {
                                    radius: 2,  // 포인트 크기 설정 (기본값은 3)
                                    hoverRadius: 5,  // 마우스를 올렸을 때 포인트 크기
                                },
                            },
                        }}
                        width={800}
                        height={400}
                    />
                )
            ) : (
                <p style={{ textAlign:'center' }}>날짜를 선택하고 검색을 눌러주세요.</p>
            )}
        </div>
    );
};












// ✅ TableComponent (월간용) — 단위/합계 로직 수정본
const TableComponent = ({ displayedData, viewMode, ivList }) => {
  if (!displayedData || !displayedData.labels) {
    return <p>데이터가 없습니다.</p>;
  }

  // 🆕 NEW: 라벨 기반 타입 판별 (단위 분기)
  const isIrr = (label = "") => label.includes("일사량");   // W/m²
  const isTemp = (label = "") => label.includes("온도");     // ℃
  const isEnergy = (label = "") => !(isIrr(label) || isTemp(label)); // Wh(발전량)

  // 🆕 NEW: 헤더/셀 단위 처리
  const headerUnit = (label) => {
    if (isIrr(label)) return "(W/m²)";
    if (isTemp(label)) return "(℃)";
    return "(kWh)"; // 에너지
  };

  const formatCell = (label, value) => {
    const v = Number(value ?? 0);
    if (isIrr(label)) return `${v.toFixed(0)} W/m²`;     // 정수 표기
    if (isTemp(label)) return `${v.toFixed(1)} ℃`;       // 소수1
    return `${(v / 1000).toFixed(2)} kWh`;               // Wh → kWh
  };

  // 동일: 전체 용량 합계(kW)
  const totalCapacity = ivList.iv_capa.reduce((sum, capa) => sum + (capa || 0), 0);

  // 🔧 CHANGED: 행 합계(Wh)는 에너지 데이터만 합산
  const rowSumsWh = displayedData.labels.map((_, rowIndex) =>
    displayedData.datasets.reduce((sum, ds) => {
      if (!isEnergy(ds.label)) return sum;                 // 환경센서 제외
      const val = Number(ds.data?.[rowIndex] ?? 0);
      return sum + val;
    }, 0)
  );

  // 🔧 CHANGED: 각 행의 발전시간(h) = (해당 월 총 Wh / 1000) / 총 kW
  const rowGenerationHours = rowSumsWh.map((sumWh) =>
    totalCapacity > 0 ? (sumWh / 1000) / totalCapacity : 0
  );

  // 🔧 CHANGED: 컬럼 합계 — 에너지 컬럼만 kWh 합계 표시, 센서 컬럼은 공백
  const columnSumsDisplay = displayedData.datasets.map((ds) => {
    if (!isEnergy(ds.label)) return ""; // 필요 시 월평균 등으로 바꿀 수 있음
    const sumWh = ds.data.reduce((s, v) => s + Number(v ?? 0), 0);
    return `${(sumWh / 1000).toFixed(2)} kWh`;
  });

  // 🔧 CHANGED: 전체 발전시간(h)도 에너지 합으로 계산
  const totalWh = rowSumsWh.reduce((s, v) => s + v, 0);
  const totalGenerationHours =
    totalCapacity > 0 ? (totalWh / 1000) / totalCapacity : 0;

  // 🔧 CHANGED: 개별 보기일 때만 행 합계(kWh) 표시
  const rowSumKWhDisplay = (rowIndex) =>
    `${(rowSumsWh[rowIndex] / 1000).toFixed(2)} kWh`;

  const grandTotalKWhDisplay = `${(totalWh / 1000).toFixed(2)} kWh`;

  return (
    <div className="table-container">
      <table className="styled-table">
        <thead>
          <tr>
            <th>날짜</th>
            {/* 🆕 NEW: 헤더에 라벨별 단위 표기 */}
            {displayedData.datasets.map((dataset, index) => (
              <th key={index}>
                {dataset.label} {headerUnit(dataset.label)}
              </th>
            ))}
            <th>발전시간 (h)</th>
            {viewMode === "individual" && <th>합계 (kWh)</th>}
          </tr>
        </thead>
        <tbody>
          {displayedData.labels.map((label, rowIndex) => (
            <tr key={rowIndex}>
              <td>{label}</td>
              {/* 🔧 CHANGED: 셀 단위/값 포맷 일관 처리 */}
              {displayedData.datasets.map((dataset, colIndex) => (
                <td key={colIndex}>
                  {formatCell(dataset.label, dataset.data?.[rowIndex])}
                </td>
              ))}
              <td>
                <strong>{rowGenerationHours[rowIndex].toFixed(2)} h</strong>
              </td>
              {viewMode === "individual" && (
                <td>
                  <strong>{rowSumKWhDisplay(rowIndex)}</strong>
                </td>
              )}
            </tr>
          ))}
          <tr className="total-row">
            <td>
              <strong>합계</strong>
            </td>
            {/* 🔧 CHANGED: 에너지 컬럼만 kWh 합계, 센서는 공백 */}
            {columnSumsDisplay.map((txt, idx) => (
              <td key={idx}>
                <strong>{txt}</strong>
              </td>
            ))}
            <td>
              <strong>{totalGenerationHours.toFixed(2)} h</strong>
            </td>
            {viewMode === "individual" && (
              <td>
                <strong>{grandTotalKWhDisplay}</strong>
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};




















export default GenMonth;
 